import type {
  ProductHighlightAdmin,
  ProductHighlightDetail,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT_PRODUCT_HIGHLIGHT } from '../constant';
import type {
  CreateProductHighlightPayload,
  UpdateProductHighlightPayload,
} from '../types';

type ProductHighlightDetailCache =
  | (ProductHighlightDetail & {
      type?: 'general' | 'specific';
      shiptos?: string | File | null;
    })
  | undefined;

const ProductHighlightQueryKey = 'ProductHighlightsList';

export const useProductHighlights = () => {
  const token = useAuthStore((s) => s.access_token);
  return useQuery({
    queryKey: [ProductHighlightQueryKey],
    queryFn: async () => {
      const { data } = await apiInstance(token).get(ENDPOINT_PRODUCT_HIGHLIGHT);
      return data as ProductHighlightAdmin;
    },
  });
};

export const useCreateProductHighlight = () => {
  const token = useAuthStore((s) => s.access_token);
  return useMutation<
    CreateProductHighlightPayload,
    Error,
    CreateProductHighlightPayload
  >({
    mutationFn: async (payload: CreateProductHighlightPayload) => {
      const { data } = await apiInstance(token).post(
        ENDPOINT_PRODUCT_HIGHLIGHT,
        payload
      );
      return data as CreateProductHighlightPayload;
    },
  });
};

export const useDeleteProductHighlight = () => {
  const token = useAuthStore((s) => s.access_token);
  return useMutation<unknown, Error, string>({
    mutationFn: async (id: string) => {
      const { data } = await apiInstance(token).delete(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${id}`
      );
      return data as unknown;
    },
  });
};

export const useReorderProductHighlight = () => {
  const token = useAuthStore((s) => s.access_token);
  return useMutation<unknown, Error, { id: string; index: number }>({
    mutationFn: async (variables: { id: string; index: number }) => {
      const { id, index } = variables;
      const { data } = await apiInstance(token).patch(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${id}/reorder`,
        { index }
      );
      return data as unknown;
    },
  });
};

export const useUpdateProductHighlight = () => {
  const token = useAuthStore((s) => s.access_token);
  const queryClient = useQueryClient();
  return useMutation<
    UpdateProductHighlightPayload,
    Error,
    { id: string; payload: UpdateProductHighlightPayload }
  >({
    mutationFn: async (variables: {
      id: string;
      payload: UpdateProductHighlightPayload;
    }) => {
      const { id, payload } = variables;
      const { data } = await apiInstance(token).patch(
        `${ENDPOINT_PRODUCT_HIGHLIGHT}/${id}`,
        payload
      );
      return data as UpdateProductHighlightPayload;
    },
    onSuccess: (_data, variables) => {
      const { id, payload } = variables;
      // Optimistically update the detail cache so the next modal open has fresh values
      queryClient.setQueryData(
        ['ProductHighLightDetail', id],
        (prev: ProductHighlightDetailCache) => {
          if (!prev) return prev;
          return {
            ...prev,
            title: payload.title !== undefined ? payload.title : prev.title,
            distribution_channel:
              payload.distribution_channel ?? prev.distribution_channel,
            business_segmentation:
              payload.business_segmentation ?? prev.business_segmentation,
            product_highlight_type:
              payload.product_highlight_type ?? prev.product_highlight_type,
            type: payload.type ?? prev.type,
            business_types: payload.business_types ?? prev.business_types,
            shiptos: payload.shiptos ?? prev.shiptos,
          };
        }
      );

      // Invalidate to confirm with server state
      queryClient.invalidateQueries({
        queryKey: ['ProductHighLightDetail', id],
        exact: true,
      });
    },
  });
};

// Export item management hooks
export {
  useAddHighlightItems,
  useDeleteHighlightItems,
  useReorderHighlightItem,
} from './items';
